import { useEffect, useMemo, useState } from 'react';
import Button from '../../ui/Button/Button';
import Icon from '../../ui/Icon/Icon';
import { estimateScenePages, estimateTotalPages, formatRuntime } from '../../../lib/screenplayEstimate';
import styles from './ChapterList.module.css';

// Title parser for screenplay scene headings: `INT. KITCHEN - NIGHT` etc.
const SCENE_RE = /^(INT|EXT|INT\/EXT|I\/E)\.?\s+(.+?)\s*-\s*(.+?)\s*$/i;
// Character cue regexes: legacy `@@CHR NAME` line + inline `<char>NAME</char>` wrapper.
const CHR_RE = /^[\t ]*@@CHR\s+(.+?)\s*$/gim;
const CHR_INLINE_RE = /<char>\s*([^<]+?)\s*<\/char>/gi;

function parseSceneTitle(title) {
  if (!title) return null;
  const m = SCENE_RE.exec(title.trim());
  if (!m) return null;
  return {
    kind: m[1].toUpperCase(),
    location: m[2].trim().toUpperCase(),
    time: m[3].trim().toUpperCase(),
  };
}

function extractCharacters(text) {
  const names = new Set();
  if (!text) return [];
  CHR_RE.lastIndex = 0;
  let m;
  while ((m = CHR_RE.exec(text)) !== null) {
    const n = m[1].trim().toUpperCase();
    if (n) names.add(n);
  }
  CHR_INLINE_RE.lastIndex = 0;
  while ((m = CHR_INLINE_RE.exec(text)) !== null) {
    const n = m[1].trim().toUpperCase();
    if (n) names.add(n);
  }
  return Array.from(names).sort();
}

function groupByParent(chapters) {
  const byParent = {};
  for (const c of chapters) {
    const key = c.parentId || 'root';
    (byParent[key] ||= []).push(c);
  }
  for (const list of Object.values(byParent)) {
    list.sort((a, b) => a.position - b.position);
  }
  return byParent;
}

// DFS-flatten a tree rooted at `roots` into the full ordered list of IDs.
function flattenTree(roots, byParent) {
  const out = [];
  const walk = (node) => {
    out.push(node.id);
    const kids = byParent[node.id] || [];
    for (const k of kids) walk(k);
  };
  for (const r of roots) walk(r);
  return out;
}

function ChapterNode({
  node,
  byParent,
  depth,
  selectedId,
  onSelect,
  onAddSub,
  onPatch,
  onDelete,
  isScreenplay,
  contentMap,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  isDragTarget,
}) {
  const children = byParent[node.id] || [];
  const [title, setTitle] = useState(node.title || '');
  const [description, setDescription] = useState(node.description || '');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setTitle(node.title || '');
  }, [node.title]);
  useEffect(() => {
    setDescription(node.description || '');
  }, [node.description]);

  const content = contentMap[node.id] || '';
  const parsed = useMemo(
    () => (isScreenplay ? parseSceneTitle(node.title) : null),
    [isScreenplay, node.title],
  );
  const characters = useMemo(
    () => (isScreenplay && expanded ? extractCharacters(content) : []),
    [isScreenplay, expanded, content],
  );
  const sceneRuntime = useMemo(
    () => (isScreenplay && expanded ? formatRuntime(estimateScenePages(content, node.title)) : null),
    [isScreenplay, expanded, content, node.title],
  );

  const handleTitleBlur = async () => {
    if (title === node.title) return;
    try {
      await onPatch(node.id, { title });
    } catch (error) {
      console.error('Erro ao atualizar título do capítulo:', error);
      setTitle(node.title || '');
    }
  };

  const handleDescBlur = async () => {
    if (description === (node.description || '')) return;
    try {
      await onPatch(node.id, { description });
    } catch (error) {
      console.error('Erro ao atualizar descrição do capítulo:', error);
      setDescription(node.description || '');
    }
  };

  const handleHideToggle = async (e) => {
    e.stopPropagation();
    try {
      await onPatch(node.id, { hidden: !node.hidden });
    } catch (error) {
      console.error('Erro ao alterar visibilidade do capítulo:', error);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    const label = isScreenplay ? 'cena' : 'capítulo';
    if (!window.confirm(`Apagar ${label} "${node.title || 'sem título'}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await onDelete(node.id);
    } catch (error) {
      console.error('Erro ao apagar capítulo:', error);
      window.alert('Falha ao apagar. Tente de novo.');
    }
  };

  const jumpCharacter = (name) => {
    onSelect(node.id);
    window.dispatchEvent(
      new CustomEvent('screenplay:jump-character', { detail: { name } }),
    );
  };

  return (
    <li
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart(e, node.id) : undefined}
      onDragOver={draggable ? (e) => onDragOver(e, node.id) : undefined}
      onDrop={draggable ? (e) => onDrop(e, node.id) : undefined}
      className={`${node.hidden ? styles.hidden : ''} ${isDragTarget ? styles.dragOver : ''}`}
    >
      <div
        className={`${styles.item} ${node.id === selectedId ? styles.active : ''}`}
        style={{ paddingLeft: `calc(var(--space-2) + ${depth * 14}px)` }}
      >
        {draggable && (
          <span
            className={styles.grip}
            title="Drag to reorder"
            onClick={() => onSelect(node.id)}
          >
            <Icon name="GripVertical" size={12} />
          </span>
        )}
        {isScreenplay ? (
          <button
            type="button"
            className={styles.chevron}
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse scene' : 'Expand scene'}
          >
            <Icon name={expanded ? 'ChevronDown' : 'ChevronRight'} size={12} />
          </button>
        ) : null}

        <button type="button" className={styles.itemBtn} onClick={() => onSelect(node.id)}>
          {!isScreenplay && (
            <span className={styles.bullet}>{children.length ? '▾' : '›'}</span>
          )}
          <input
            type="text"
            className={styles.itemTitleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
            onClick={(e) => e.stopPropagation()}
          />
        </button>
        <button
          type="button"
          className={styles.rowAction}
          title={node.hidden ? 'Mostrar' : 'Ocultar'}
          aria-label={node.hidden ? 'Mostrar' : 'Ocultar'}
          onClick={handleHideToggle}
        >
          <Icon name={node.hidden ? 'EyeOff' : 'Eye'} size={12} />
        </button>
        <button
          type="button"
          className={`${styles.rowAction} ${styles.rowActionDanger}`}
          title="Apagar"
          aria-label="Apagar"
          onClick={handleDelete}
        >
          <Icon name="Trash2" size={12} />
        </button>
        {!isScreenplay && (
          <button
            type="button"
            className={styles.addSub}
            title="Add subchapter"
            aria-label="Add subchapter"
            onClick={() => onAddSub(node.id)}
          >
            <Icon name="Plus" size={12} />
          </button>
        )}
      </div>

      {isScreenplay && expanded && (
        <div
          className={styles.details}
          style={{ marginLeft: `${14 * depth + 22}px` }}
        >
          {parsed ? (
            <div className={styles.badges}>
              <span className={styles.badgeKind} data-kind={parsed.kind}>{parsed.kind}</span>
              <span className={styles.badgeLoc}>{parsed.location}</span>
              <span className={styles.badgeTime}>{parsed.time}</span>
              {sceneRuntime && <span className={styles.badgeRuntime}>{sceneRuntime}</span>}
            </div>
          ) : (
            <div className={styles.badges}>
              <p className={styles.detailsHint}>No scene heading. Rename to `INT./EXT. LOCATION - TIME`.</p>
              {sceneRuntime && <span className={styles.badgeRuntime}>{sceneRuntime}</span>}
            </div>
          )}

          <div className={styles.detailsLabel}>Description</div>
          <textarea
            className={styles.descInput}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescBlur}
            placeholder="Scene metadata description"
            rows={2}
          />

          <div className={styles.detailsLabel}>Characters</div>
          {characters.length === 0 ? (
            <p className={styles.detailsHint}>No characters yet. Use `@@CHR NAME` in the body.</p>
          ) : (
            <div className={styles.chars}>
              {characters.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={styles.charChip}
                  onClick={() => jumpCharacter(name)}
                  title={`Jump to ${name}`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {children.length > 0 && (
        <ul className={styles.list}>
          {children.map((child) => (
            <ChapterNode
              key={child.id}
              node={child}
              byParent={byParent}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onAddSub={onAddSub}
              onPatch={onPatch}
              onDelete={onDelete}
              isScreenplay={isScreenplay}
              contentMap={contentMap}
              draggable={false}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function ChapterList({
  chapters,
  selectedId,
  onSelect,
  onCreate,
  onPatch,
  onDelete,
  onReorder,
  codexType,
  chapterContents = {},
}) {
  const byParent = groupByParent(chapters);
  const roots = byParent.root || [];
  const isScreenplay = codexType === 'screenplay';
  const label = isScreenplay ? 'Scenes' : 'Chapters';
  const itemNoun = isScreenplay ? 'scene' : 'chapter';

  const totalRuntime = useMemo(() => {
    if (!isScreenplay) return null;
    const visible = chapters.filter((c) => !c.hidden);
    if (visible.length === 0) return null;
    const scenes = visible.map((c) => ({ title: c.title, content: chapterContents[c.id] || '' }));
    return formatRuntime(estimateTotalPages(scenes));
  }, [isScreenplay, chapters, chapterContents]);

  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* Firefox quirk */ }
  };
  const handleDragOver = (e, id) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const src = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!src || src === targetId) return;
    const rootIds = roots.map((r) => r.id);
    const fromIdx = rootIds.indexOf(src);
    const toIdx = rootIds.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = rootIds.slice();
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, src);
    const newRoots = next.map((id) => roots.find((r) => r.id === id));
    const ordered = flattenTree(newRoots, byParent);
    onReorder?.(ordered);
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.heading}>{label}</span>
          {totalRuntime && <span className={styles.runtime}>{totalRuntime}</span>}
        </div>
        <Button variant="ghost" onClick={() => onCreate(null)} aria-label={`New ${itemNoun}`}>
          <Icon name="Plus" size={13} />
        </Button>
      </div>

      {roots.length === 0 && <p className={styles.empty}>No {itemNoun}s yet.</p>}

      <ul className={styles.list}>
        {roots.map((node) => (
          <ChapterNode
            key={node.id}
            node={node}
            byParent={byParent}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            onAddSub={onCreate}
            onPatch={onPatch}
            onDelete={onDelete}
            isScreenplay={isScreenplay}
            contentMap={chapterContents}
            draggable={true}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            isDragTarget={dragOverId === node.id}
          />
        ))}
      </ul>
    </aside>
  );
}
